import React from 'react';
import { Button } from 'react-bootstrap';
import Select from 'react-select';
import '../components/PlayerComponents.css';


interface Team {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  ageGroups: string[];
  coachManagerName: string;
  contactDetails: string;
}

interface PlayersHeaderProps {
  team: Team | null;
  title: string;
  description: string;
  searchTerm: string;
  onSearchChange: (searchTerm: string) => void;
  ageCategoryFilter: string;
  onAgeCategoryFilterChange: (filter: string) => void;
  onAddPlayer: () => void;
  onGoBack: () => void;
  addButtonText?: string;
  showFilters?: boolean;
}

const PlayersHeader: React.FC<PlayersHeaderProps> = ({
  team,
  title,
  description,
  searchTerm,
  onSearchChange,
  ageCategoryFilter,
  onAgeCategoryFilterChange,
  onAddPlayer,
  onGoBack,
  addButtonText = "Add New Player",
  showFilters = true
}) => {
  // Age category options for react-select
  const ageCategoryOptions = [
    { value: 'all', label: 'All' },
    { value: 'U7', label: 'U7' },
    { value: 'U10', label: 'U10' },
    { value: 'U11', label: 'U11' },
    { value: 'U12', label: 'U12' },
    { value: 'U13', label: 'U13' },
    { value: 'U14', label: 'U14' },
    { value: 'U15', label: 'U15' },
    { value: 'U16', label: 'U16' },
    { value: 'U17', label: 'U17' },
    { value: 'U18', label: 'U18' }
  ];

  return (
    <div className="page-header-section mb-4" style={{ position: 'relative', zIndex: 10 }}>
      {/* Row 1: Back Button, Team Logo, Team Name - Left Aligned */}
      <div className="d-flex align-items-center mb-4" style={{ gap: '16px', position: 'relative', zIndex: 10, marginBottom: '24px !important', justifyContent: 'flex-start', width: '100%' }}>
        <button 
          className="back-button"
          onClick={onGoBack}
          style={{ position: 'relative', zIndex: 10 }}
        >
          <i className="ri-arrow-left-line"></i>
        </button>
        <div className="team-logo-small" style={{ flexShrink: 0, position: 'relative', zIndex: 10 }}>
          {team?.logo ? (
            <img 
              src={team.logo}
              alt="Team Logo" 
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.nextElementSibling?.classList.remove('d-none');
              }}
            />
          ) : null}
          {!team?.logo && (
            <div className="default-team-logo-small">
              <i className="ri-team-line"></i>
            </div>
          )}
        </div>
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
          <h1 className="page-title mb-0" style={{ position: 'relative', zIndex: 10 }}>{team?.name}</h1>
          {team?.ageGroups && team.ageGroups.length > 0 && (
            <div className="d-flex align-items-center" style={{ gap: '4px' }}>
              {team.ageGroups.length === 1 ? (
                <span className="age-category-badge">{team.ageGroups[0]}</span>
              ) : (
                team.ageGroups.map((ageGroup, index) => (
                  <span key={index} className="age-category-badge">{ageGroup}</span>
                ))
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Row 2: Title/Subtext on Left, Filters and Add Button on Right */}
      <div className="d-flex justify-content-between align-items-start" style={{ position: 'relative', zIndex: 10, display: 'flex', width: '100%' }}>
        {/* Left: Players Title and Description */}
        <div className="players-section-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: '0 0 auto' }}>
          <h4 className="section-title mb-2" style={{ fontSize: '20px', fontWeight: '600', color: '#495057', margin: '0 0 8px 0', lineHeight: '1.3' }}>{title}</h4>
          <p className="section-description mb-0" style={{ fontSize: '14px', color: '#6c757d', margin: '0', lineHeight: '1.4' }}>{description}</p>
        </div>
        
        {/* Right: Filters and Add Button */}
        <div className="filter-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-end' }}>
          {showFilters && (
            <>
              <div className="inline-filters d-flex align-items-center gap-2">
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    className="form-search"
                    placeholder="Search Players"
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    style={{ width: '200px', paddingRight: '35px' }}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="clear-search-btn"
                      onClick={() => onSearchChange('')}
                      title="Clear search"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
                <Select
                  classNamePrefix="react-select"
                  value={ageCategoryOptions.find(option => option.value === ageCategoryFilter)}
                  onChange={(selectedOption) => onAgeCategoryFilterChange(selectedOption?.value || 'all')}
                  options={ageCategoryOptions}
                  placeholder="All"
                  styles={{
                    control: (base) => ({ 
                      ...base, 
                      minHeight: '40px', 
                      width: '120px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      boxShadow: 'none'
                    }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  isSearchable={false}
                />
              </div>
            </>
          )}
          <Button 
            variant="outline-primary" 
            onClick={onAddPlayer}
            className="add-player-btn"
            style={{ position: 'relative', zIndex: 15 }}
          >
            {addButtonText} <i className="ri-add-line"></i>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlayersHeader; 